import {
  Box,
  useToast
} from "@chakra-ui/react";
import React, {
  useCallback,
  useEffect,
  useState
} from "react";
import { useForm } from "react-hook-form";
import {
  useHistory,
  useParams
} from "react-router-dom";
import { isPast } from "date-fns";

import { useConversationsApi } from "src/api/conversations/conversations.api";
import { Header } from "src/components/shared/Header";
import { Text } from "src/components/text";
import { ConversationMode } from "src/api";
import { pickerDateToUtcKey } from "src/utils/utcDate";

import {
  CreateEditConversationForm,
  FormProps,
  SelectModeOption
} from "../create/components/CreateEditConversationForm";

interface PageParams {
  conversationId: string
}

export const EditConversationPage: React.FC = () => {
  const toast = useToast();
  const history = useHistory();
  const { conversationId } = useParams<PageParams>();
  const [ conversationRes, conversationReq ] = useConversationsApi("GET_CONVERSATION");
  const [ selectedPartner, setSelectedParter ] = useState<string>();
  const [ updateConversationRes, updateConversationReq ] = useConversationsApi("UPDATE_CONVERSATION");

  const [ mode, setMode ] = useState<SelectModeOption>({
    label: "Message",
    value: ConversationMode.MESSAGE
  });

  useEffect(() => {
    conversationReq({ id: conversationId });
  }, [ conversationReq, conversationId ]);

  const {
    register, handleSubmit, reset, watch, setValue
  } = useForm<FormProps>();

  useEffect(() => {
    if (conversationRes.data) {
      const startTime = new Date(conversationRes.data.startTime);
      const endTime = new Date(conversationRes.data.endTime);
      const date = new Date(conversationRes.data.date);

      reset({
        date,
        endTime,
        startTime,
        latencyMinutes: Math.floor(conversationRes.data.latencySeconds / 60),
        latencySeconds: conversationRes.data.latencySeconds % 60
      });

      if (conversationRes.data.participants?.[ 0 ].user) {
        setSelectedParter(conversationRes.data.participants?.[ 0 ].user._id);
      }

      if (conversationRes.data.mode) {
        setMode({
          label: conversationRes.data.mode,
          value: conversationRes.data.mode
        });
      }
    }

    if (conversationRes.error) {
      toast({
        status: "error",
        title: "Error getting conversation",
        description: conversationRes.error.details
      });
    }
  }, [
    conversationRes,
    reset,
    toast
  ]);

  useEffect(() => {
    if (updateConversationRes.data) {
      const defaultTopic = updateConversationRes.data.topics?.[ 0 ];

      if (!updateConversationRes.data._id || !defaultTopic) {
        toast({
          title: "Error updating conversation",
          description: "Conversation was updated but couldn't get topic ID",
          status: "error"
        });
      } else {
        history.push(`/conversation/${updateConversationRes.data._id}`);
      }
    }

    if (updateConversationRes.error) {
      toast({
        title: "Error submitting form",
        description: updateConversationRes.error.details,
        status: "error"
      });
    }
  }, [
    updateConversationRes,
    toast,
    history
  ]);

  const onSubmit = useCallback((data: FormProps) => {
    if (selectedPartner) {
      const latencyDate = pickerDateToUtcKey(data.date);
      const startTime = data.date;

      startTime.setHours(data.startTime.getHours());
      startTime.setMinutes(data.startTime.getMinutes());
      startTime.setSeconds(0);

      const endTime = new Date(data.date);

      endTime.setHours(data.endTime.getHours());
      endTime.setMinutes(data.endTime.getMinutes());
      endTime.setSeconds(0);

      if (isPast(endTime) || isPast(startTime)) {
        toast({
          title: "Error submitting form",
          description: "Your dates / times have already past",
          status: "error"
        });
      } else {
        updateConversationReq({
          _id: conversationId,
          date: new Date(data.date).toISOString(),
          latencyDate,
          latencySeconds: (data.latencyMinutes || 0) * 60 + (data.latencySeconds || 0),
          startTime: startTime.toISOString(),
          endTime: endTime.toISOString()
        });
      }
    } else {
      toast({
        title: "Invite somebody",
        description: "Please select a partner to continue",
        status: "error"
      });
    }
  }, [
    conversationId,
    updateConversationReq,
    selectedPartner,
    toast
  ]);

  return (
    <Box p={10}>
      <Header />

      <Box paddingY={10}>
        <Text as="h1">
          Edit session
        </Text>
      </Box>

      {conversationRes.complete && (
        <CreateEditConversationForm
          setMode={setMode}
          mode={mode}
          register={register}
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
          setSelectedParter={setSelectedParter}
          selectedPartner={selectedPartner}
          isNew={false}
          watch={watch}
          setValue={setValue}
        />
      )}

    </Box>
  );
};